import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import { resetPasswordConfirmAsync } from "../features/authentication/authenticationApi";
import { HttpStatusCode } from "axios";
import { StringExtension } from "../features/extensions/StringExtension";
import { ROUTES } from "../routes";
import { useTranslation } from 'react-i18next';
import Input from "../components/input/Input";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stringExtension = new StringExtension();

  const [email, setEmail] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [repeatPassword, setRepeatPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | null>(
    null
  );
  const [showSuccessNotification, setShowSuccessNotification] =
    useState<boolean>(false);

  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");

    if (emailParam) setEmail(emailParam);
    if (tokenParam) setToken(tokenParam);
  }, [searchParams]);

  const validatePassword = (password: string): boolean => {
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNonAlphanumeric = /[^a-zA-Z0-9]/.test(password);
    return hasLowerCase && hasUpperCase && hasNonAlphanumeric;
  };

  const handleResetPassword = async () => {
    setError(null);
    setPasswordError(null);
    setRepeatPasswordError(null);

    if (stringExtension.isEmpty(newPassword)) {
      setPasswordError(t('auth.errors.passwordRequired'));
      return;
    }

    if (!validatePassword(newPassword)) {
      setPasswordError(t('auth.errors.passwordTooWeak'));
      return;
    }

    if (stringExtension.isEmpty(repeatPassword)) {
      setRepeatPasswordError(t('auth.errors.repeatPasswordRequired'));
      return;
    }

    if (newPassword !== repeatPassword) {
      setRepeatPasswordError(t('auth.errors.passwordsNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const resultAction = await dispatch(
        resetPasswordConfirmAsync({
          email,
          token,
          newPassword,
        })
      );

      if (resetPasswordConfirmAsync.fulfilled.match(resultAction)) {
        setShowSuccessNotification(true);
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 2000);
      } else if (resetPasswordConfirmAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        if (errorResponse) {
          switch (errorResponse.status) {
            case HttpStatusCode.BadRequest:
              setError(errorResponse.message || t('auth.errors.unknownError'));
              break;
            case HttpStatusCode.NotFound:
              setError(t('auth.errors.invalidToken'));
              break;
            case HttpStatusCode.InternalServerError:
              setError(t('auth.errors.serverError'));
              break;
            default:
              setError(errorResponse.message || t('auth.errors.unknownError'));
              break;
          }
        } else {
          setError(t('auth.errors.unknownError'));
        }
      }
    } catch (error) {
      setError(t('auth.errors.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center px-4" style={{ height: "calc(100vh - var(--header-height, 80px))" }}>
      <div
        className="max-w-md w-full p-6 sm:p-8 rounded-2xl bg-white dark:bg-primary-20 shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)] flex flex-col items-center text-center h-fit relative"
      >
        <h1 className="text-2xl font-bold mb-2 text-grey-10 dark:text-white">
          {t('resetPasswordPage.title')}
        </h1>

        <p className="text-grey-50 dark:text-grey-70 mb-6">
          {t('resetPasswordPage.subtitle')}
        </p>

        <Input
          label={t('resetPasswordPage.newPassword')}
          type="password"
          name="newPassword"
          id="newPassword"
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            if (passwordError) setPasswordError(null);
          }}
          disabled={loading}
          error={!!passwordError}
          helperText={passwordError || ''}
          showPasswordToggle={true}
          labelPosition="top-left"
        />

        <Input
          label={t('resetPasswordPage.confirmPassword')}
          type="password"
          name="repeatPassword"
          id="repeatPassword"
          fullWidth
          margin="normal"
          value={repeatPassword}
          onChange={(e) => {
            setRepeatPassword(e.target.value);
            if (e.target.value !== newPassword) {
              setRepeatPasswordError(t('auth.errors.passwordsNotMatch'));
            } else {
              setRepeatPasswordError(null);
            }
          }}
          disabled={loading}
          error={!!repeatPasswordError}
          helperText={repeatPasswordError || ''}
          showPasswordToggle={true}
          labelPosition="top-left"
        />

        {error && (
          <div className="w-full mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-md">
            {error}
          </div>
        )}

        <button
          onClick={handleResetPassword}
          disabled={loading}
          className="btn btn-primary w-full h-[42px] flex items-center justify-center text-[16px] no-underline mt-6"
        >
          {t('resetPasswordPage.resetButton')}
        </button>
      </div>

      {showSuccessNotification && (
        <div className="fixed top-4 right-4 bg-green-600 dark:bg-green-700 text-white px-6 py-3 rounded-md shadow-lg z-50">
          {t('resetPasswordPage.success')}
        </div>
      )}
    </div>
  );
};

export default ResetPasswordPage;
